import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable'
import { Column as TableColumn } from 'primereact/column'
import { Card } from 'primereact/card'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useBoltContext } from '../contexts/BoltContext'
import { useExportContext } from '../contexts/ExportContext'
import { useUserContext } from '../contexts/UserContext'
import { getMenuItemExport } from '../common/menu-items'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import { TagIdCopyButton } from '../common/pure/CopyButton'
import DateDisplay from '../common/pure/DateDisplay'
import Heading from '../common/pure/Heading'
import { TooltipAvatar } from '../common/ProfileAvatar'
import ExportDialog from '../dialogs/ExportDialog'
import ContextHeader from '../common/ContextHeader'
import Bolt from '../../models/Bolt'
import {
    getFilterableDateRangeColumnProps,
    getFilterableEnumColumnProps,
    getFilterableNumericRangeColumnProps,
    getFilterableTableProps,
    getSortableColumnProps,
    getSortableTableProps,
} from '../table/table-props'
import TablePagination from '../table/TablePagination'
import { ArchiveButton, RestoreButton, TableActionContainer } from '../table/TableButton'
import { BoltDiameterSettings, BoltTypeSettings, MaterialSettings } from '../../tools/settings'
import { StructureTypes } from '../../models/Structure'
import { buildStructureLink } from '../../tools/link'
import { mm } from '../../tools/format'
import t, { _ta, _tf } from '../../tools/t'

export default function ManufacturedBoltsScreen() {
    const navigate = useNavigate()
    const [exportVisible, setExportVisible] = useState(false)
    const { manufacturedBolts, archiveBolt, restoreBolt } = useBoltContext()
    const { exportManufactured } = useExportContext()
    const { can } = useUserContext()
    const canWriteHardware = can(PermissionSuffix.HardwareWrite)

    const navigateToBolt = (event: DataTableRowClickEvent) => {
        const bolt = event.data as Bolt
        const link = buildStructureLink(StructureTypes.Bolt, bolt.id)
        navigate(link)
    }

    const exportManufacturedRange = (start: Date, end: Date) =>
        exportManufactured(manufacturedBolts, start, end)

    const menu = [getMenuItemExport(() => setExportVisible(true), t('common_download_x', 'CSV'))]

    const onArchiveBolt = async (bolt: Bolt) => {
        if (!confirm('Are you sure you want to archive bolt:\n' + bolt.name)) return
        await archiveBolt(bolt.id)
    }

    const onRestoreBolt = (bolt: Bolt) => restoreBolt(bolt.id)

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <ContextHeader menu={menu}>
                        <Heading standalone size={2}>
                            {t('manufactured_bolts_list')}
                        </Heading>
                    </ContextHeader>

                    <ExportDialog
                        visible={exportVisible}
                        onHide={() => setExportVisible(false)}
                        onExport={exportManufacturedRange}
                    />

                    <Card>
                        <DataTable
                            value={manufacturedBolts.items}
                            rowClassName={(b: Bolt) =>
                                b.isArchived ? 'archived-table-row' : undefined
                            }
                            rowHover={true}
                            emptyMessage={t('hardware_empty')}
                            onRowClick={navigateToBolt}
                            {...getSortableTableProps(manufacturedBolts)}
                            {...getFilterableTableProps()}
                        >
                            <TableColumn
                                header={t('common_tag')}
                                body={(b: Bolt) => <TagIdCopyButton tag={b.currentTag} />}
                            />

                            <TableColumn
                                header={t('common_user')}
                                body={(b: Bolt) => <TooltipAvatar id={b.createdBy} target={b.id} />}
                            />

                            <TableColumn
                                header={t('bolt_field_batch_no')}
                                body={(b: Bolt) => b.manufacturingDetails?.batch}
                                {...getSortableColumnProps('manufacturingDetails.batch')}
                            />

                            <TableColumn
                                header={t('bolt_field_diameter')}
                                body={(b: Bolt) => _ta(b, 'diameter')}
                                {...getFilterableEnumColumnProps(
                                    'diameter',
                                    manufacturedBolts,
                                    BoltDiameterSettings,
                                    true
                                )}
                            />

                            <TableColumn
                                header={t('bolt_field_length')}
                                body={(b: Bolt) => mm(b.nominalLength)}
                                {...getFilterableNumericRangeColumnProps(
                                    'nominalLength',
                                    manufacturedBolts
                                )}
                            />

                            <TableColumn
                                header={t('bolt_field_material')}
                                body={(b: Bolt) => _tf('', 'material', b.material)}
                                {...getFilterableEnumColumnProps(
                                    'material',
                                    manufacturedBolts,
                                    MaterialSettings,
                                    true
                                )}
                            />

                            <TableColumn
                                header={t('bolt_field_type')}
                                body={(b: Bolt) => _ta(b, 'type')}
                                {...getFilterableEnumColumnProps(
                                    'type',
                                    manufacturedBolts,
                                    BoltTypeSettings,
                                    true
                                )}
                            />

                            <TableColumn
                                header={t('life_cycle_field_created_at')}
                                body={(b: Bolt) => (
                                    <DateDisplay value={b.createdAt} target={b.id} />
                                )}
                                {...getSortableColumnProps('createdAt')}
                                {...getFilterableDateRangeColumnProps(
                                    'createdAt',
                                    manufacturedBolts
                                )}
                            />

                            {canWriteHardware && (
                                <TableColumn
                                    body={(b: Bolt) => (
                                        <TableActionContainer>
                                            {b.isArchived ? (
                                                <RestoreButton
                                                    target={b.id}
                                                    onClick={() => onRestoreBolt(b)}
                                                />
                                            ) : (
                                                <ArchiveButton
                                                    target={b.id}
                                                    onClick={() => onArchiveBolt(b)}
                                                />
                                            )}
                                        </TableActionContainer>
                                    )}
                                />
                            )}
                        </DataTable>
                        <TablePagination data={manufacturedBolts} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
